package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.ui.quizSelectionPendingBg
import com.shadowings.gero.presentation.ui.quizSelectionWrongBg
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.rememberDeviceInfo

// Shared by the phonetic-keyboard quizzes (MonoliteralPhoneticQuiz, ImageKeyboardQuiz): one slot
// per expected character, colored correct/wrong once the answer is shown.
@Composable
internal fun PhoneticAnswerSlots(
    reading: List<String>,
    selectedLetters: List<String>,
    showResult: Boolean,
    blinking: Boolean,
    isLandscape: Boolean,
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp),
        horizontalArrangement = Arrangement.Center
    ) {
        reading.forEachIndexed { index, string ->
            Box(
                modifier = Modifier.widthIn(max = if (isLandscape) 28.dp else 32.dp)
                    .padding(2.dp),
                contentAlignment = Alignment.Center
            ) {
                OutlinedCard(
                    Modifier
                        .aspectRatio(0.7f),
                    colors = CardDefaults.outlinedCardColors(
                        containerColor = if (showResult && !blinking) {
                            if (selectedLetters.size > index && selectedLetters[index].equals(
                                    string, ignoreCase = true
                                )
                            ) {
                                quizSelectionCorrectBg
                            } else {
                                quizSelectionWrongBg
                            }
                        } else if (selectedLetters.size > index) {
                            quizSelectionPendingBg
                        } else {
                            Color.Transparent
                        }
                    )
                ) {
                    Box(
                        Modifier
                            .fillMaxSize()
                    ) {
                        Text(
                            modifier = Modifier.fillMaxWidth()
                                .align(Alignment.Center),
                            text = if (selectedLetters.size > index) {
                                if (selectedLetters[index] == " ") "␣" else selectedLetters[index]
                            } else {
                                "-"
                            },
                            // Body, not title: displayFontFamily (Roboto Condensed)
                            // is missing several transliteration glyphs on web.
                            style = if (isLandscape) MaterialTheme.typography.bodyMedium else MaterialTheme.typography.bodyLarge,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}

// HieroKeyboard wired to a fixed-length answer: ⌫ deletes the last letter, ⌧ clears, ␣ types a
// space, and input stops at reading.size. In test mode the next expected key is highlighted.
@Composable
internal fun PhoneticKeyboardInput(
    reading: List<String>,
    selectedLetters: List<String>,
    onSelectedLettersChange: (List<String>) -> Unit,
    enabled: Boolean,
    testMode: Boolean,
    isLandscape: Boolean,
) {
    val deviceinfo = rememberDeviceInfo()
    val keyHeight =
        if (isLandscape || deviceinfo.deviceClass == DeviceClass.PHONE_SMALL) 40.dp else 64.dp
    HieroKeyboard(
        modifier = Modifier.fillMaxWidth(),
        enabled = enabled,
        selected = if (testMode && reading.size > selectedLetters.size) {
            reading[selectedLetters.size]
        } else null,
        keyHeight = keyHeight,
        onClick = {
            if (it == "⌫") {
                if (selectedLetters.isNotEmpty()) {
                    onSelectedLettersChange(selectedLetters.dropLast(1))
                }
            } else if (it == "⌧") {
                onSelectedLettersChange(emptyList())
            } else {
                val letter = if (it == "␣") " " else it
                if (selectedLetters.size < reading.size) {
                    onSelectedLettersChange(selectedLetters + letter)
                }
            }
        }
    )
}
